<script>
import { computed, ref, nextTick, watch } from 'vue';
import { format, parseISO } from 'date-fns';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { isValidURL } from 'dashboard/helper/URLHelper';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import AttributeInfoModal from 'dashboard/components/AttributeInfoModal.vue';

export default {
  name: 'CustomAttributesWrapper',
  components: {
    MultiselectDropdown,
    AttributeInfoModal,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    attributeType: {
      type: String,
      default: 'contact_attribute',
    },
  },
  setup(props) {
    const store = useStore();
    const getters = useStoreGetters();

    const DATE_FORMAT = 'yyyy-MM-dd';

    // Reactive state
    const editingAttribute = ref(null);
    const editedValue = ref('');
    const showAddDropdown = ref(false);
    const tempAttributes = ref([]);
    const showAttributeModal = ref(false);
    const selectedAttribute = ref(null);

    const attributes = computed(() =>
      getters['attributes/getAttributesByModel'].value(props.attributeType)
    );

// Watch for contact changes to clear temporary state
watch(() => props.contact?.id, (newId, oldId) => {
  if (newId !== oldId) {
    // Clear all temporary state when contact changes
    tempAttributes.value = [];
    editingAttribute.value = null;
    editedValue.value = '';
    showAddDropdown.value = false;
  }
});

const customAttributes = computed(() => {
  return props.contact.custom_attributes || {};
});

const attributesWithMetadata = computed(() => {
  const result = [];

  // Get defined attributes with their metadata
  attributes.value.forEach(attr => {
    const key = attr.attribute_key;
    
    // Skip text type attributes named exactly "carrinho" or "Carrinho" or "source_app_campaign"
    if ((key === 'carrinho' || key === 'Carrinho' || key === 'source_app_campaign') && attr.attribute_display_type === 'text') {
      return;
    }
    
    const hasValue = Object.prototype.hasOwnProperty.call(
      customAttributes.value,
      key
    );
    const value = hasValue ? customAttributes.value[key] : '';
    
    // Only include if has value
    // For checkbox: only include if it has been explicitly set (true or false)
    if (attr.attribute_display_type === 'checkbox') {
      if (hasValue && (value === true || value === false)) {
        result.push({
          key,
          value,
          attribute_display_name: attr.attribute_display_name || key,
          attribute_display_type: attr.attribute_display_type || 'text',
          attribute_description: attr.attribute_description || '',
          attribute_values: attr.attribute_values || [],
          regex_pattern: attr.regex_pattern || null,
          regex_cue: attr.regex_cue || null,
        });
      }
    } else if (value) {
      // For other types, only include if has value
      result.push({
        key,
        value,
        attribute_display_name: attr.attribute_display_name || key,
        attribute_display_type: attr.attribute_display_type || 'text',
        attribute_description: attr.attribute_description || '',
        attribute_values: attr.attribute_values || [],
        regex_pattern: attr.regex_pattern || null,
        regex_cue: attr.regex_cue || null,
      });
    }
  });

  // Add any additional custom attributes not defined in the model (only if they have value)
  Object.entries(customAttributes.value).forEach(([key, value]) => {
    // Skip text type attributes named exactly "carrinho" or "Carrinho" or "source_app_campaign" (undefined attributes default to text type)
    if ((key === 'carrinho' || key === 'Carrinho' || key === 'source_app_campaign')) {
      return;
    }
    
    // Skip if already in result or if no value
    // For boolean values, we need to check explicitly
    const hasValue = value !== null && value !== undefined && value !== '';
    if (hasValue && !result.some(item => item.key === key)) {
      result.push({
        key,
        value,
        attribute_display_name: key,
        attribute_display_type: 'text',
        attribute_description: '',
        attribute_values: [],
        regex_pattern: null,
        regex_cue: null,
      });
    }
  });

  return result;
});

// Show all attributes that have value plus temporary attributes
const displayedAttributes = computed(() => {
  return [...attributesWithMetadata.value, ...tempAttributes.value];
});

// Get unused attributes (attributes without value)
const unusedAttributes = computed(() => {
  return attributes.value.filter(attr => {
    // Skip text type attributes named exactly "carrinho" or "Carrinho" or "source_app_campaign"
    if ((attr.attribute_key === 'carrinho' || attr.attribute_key === 'Carrinho' || attr.attribute_key === 'source_app_campaign') && attr.attribute_display_type === 'text') {
      return false;
    }

    // Check if it's in temporary attributes
    if (tempAttributes.value.some(temp => temp.key === attr.attribute_key)) {
      return false;
    }

    const hasValue = Object.prototype.hasOwnProperty.call(
      customAttributes.value,
      attr.attribute_key
    );
    const value = hasValue ? customAttributes.value[attr.attribute_key] : '';

    // Consider empty strings, null, undefined as no value
    // But checkbox can be false and still be considered as having value
    if (attr.attribute_display_type === 'checkbox') {
      return !hasValue;
    }
    return !value;
  });
});

const getFormattedAttributeValue = (attribute) => {
  if (!attribute.value && attribute.attribute_display_type !== 'checkbox') return '';

  const type = attribute.attribute_display_type;

  if (type === 'date') {
    return attribute.value ? new Date(attribute.value).toLocaleDateString() : '';
  }
  if (type === 'checkbox') {
    return attribute.value === true || attribute.value === 'true';
  }
  if (type === 'link') {
    return attribute.value || '';
  }
  return attribute.value || '';
};

const getFormattedEditValue = (attribute) => {
  const type = attribute.attribute_display_type;
  
  if (type === 'date' && attribute.value) {
    return format(new Date(attribute.value), DATE_FORMAT);
  }
  if (type === 'checkbox') {
    return attribute.value === true || attribute.value === 'true';
  }
  return attribute.value || '';
};

const getAttributeIcon = (type) => {
  const iconMap = {
    'date': 'i-lucide-calendar',
    'checkbox': 'i-lucide-check-square',
    'link': 'i-lucide-link',
    'list': 'i-lucide-list',
    'number': 'i-lucide-hash',
    'text': 'i-lucide-type',
  };
  return iconMap[type] || 'i-lucide-tag';
};

const getInputType = (type) => {
  if (type === 'link') return 'url';
  if (type === 'number') return 'number';
  if (type === 'date') return 'date';
  return 'text';
};

const startEditing = (attribute) => {
  editingAttribute.value = attribute.key;
  editedValue.value = getFormattedEditValue(attribute);
};

const cancelEditing = () => {
  // If canceling a temp attribute, remove it
  const tempAttr = tempAttributes.value.find(t => t.key === editingAttribute.value);
  if (tempAttr) {
    tempAttributes.value = tempAttributes.value.filter(t => t.key !== editingAttribute.value);
  }
  
  editingAttribute.value = null;
  editedValue.value = '';
};

const updateCustomAttribute = async (attribute) => {
  if (!props.contact?.id) return;

  let finalValue = editedValue.value;
  
  // Don't save empty values for new attributes
  if (!finalValue && tempAttributes.value.some(t => t.key === attribute.key)) {
    // Remove from temp attributes if canceling without value
    tempAttributes.value = tempAttributes.value.filter(t => t.key !== attribute.key);
    cancelEditing();
    return;
  }
  
  // Parse date if needed
  if (attribute.attribute_display_type === 'date' && editedValue.value) {
    finalValue = parseISO(editedValue.value);
  }

  const updatedAttributes = { ...customAttributes.value, [attribute.key]: finalValue };
  
  try {
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      custom_attributes: updatedAttributes,
    });
    useAlert('Atributo atualizado com sucesso');
    
    // Remove from temp attributes after successful save
    tempAttributes.value = tempAttributes.value.filter(t => t.key !== attribute.key);
    
    cancelEditing();
  } catch (error) {
    const errorMessage =
      error?.response?.message || 'Erro ao atualizar o atributo';
    useAlert(errorMessage, 'error');
  }
};

const updateCheckbox = async (attribute, value) => {
  if (!props.contact?.id) return;

  const updatedAttributes = { ...customAttributes.value, [attribute.key]: value };
  
  try {
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      custom_attributes: updatedAttributes,
    });
    useAlert('Atributo atualizado com sucesso');
  } catch (error) {
    const errorMessage =
      error?.response?.message || 'Erro ao atualizar o atributo';
    useAlert(errorMessage, 'error');
  }
};

const updateListValue = async (attribute, selectedItem) => {
  if (!props.contact?.id || !selectedItem) return;

  // Check if it's a temporary attribute
  const isTemp = tempAttributes.value.some(t => t.key === attribute.key);
  
  if (isTemp) {
    // Update the value in the temporary attribute
    const tempAttr = tempAttributes.value.find(t => t.key === attribute.key);
    if (tempAttr) {
      tempAttr.value = selectedItem.name;
    }
    
    // Save to backend
    const updatedAttributes = { ...customAttributes.value, [attribute.key]: selectedItem.name };
    
    try {
      await store.dispatch('contacts/update', {
        id: props.contact.id,
        custom_attributes: updatedAttributes,
      });
      useAlert('Atributo atualizado com sucesso');
      
      // Remove from temp attributes after successful save
      tempAttributes.value = tempAttributes.value.filter(t => t.key !== attribute.key);
    } catch (error) {
      const errorMessage =
        error?.response?.message || 'Erro ao atualizar o atributo';
      useAlert(errorMessage, 'error');
    }
  } else {
    // For existing attributes, just update normally
    const updatedAttributes = { ...customAttributes.value, [attribute.key]: selectedItem.name };
    
    try {
      await store.dispatch('contacts/update', {
        id: props.contact.id,
        custom_attributes: updatedAttributes,
      });
      useAlert('Atributo atualizado com sucesso');
    } catch (error) {
      const errorMessage =
        error?.response?.message || 'Erro ao atualizar o atributo';
      useAlert(errorMessage, 'error');
    }
  }
};

const deleteCustomAttribute = async (key) => {
  // Check if it's a temporary attribute
  const tempAttr = tempAttributes.value.find(t => t.key === key);
  if (tempAttr) {
    // Just remove from temp attributes
    tempAttributes.value = tempAttributes.value.filter(t => t.key !== key);
    cancelEditing();
    return;
  }
  
  if (!props.contact?.id) return;
  try {
    await store.dispatch('contacts/deleteCustomAttributes', {
      id: props.contact.id,
      customAttributes: [key],
    });
    useAlert('Atributo excluído com sucesso');
  } catch (error) {
    const errorMessage =
      error?.response?.message || 'Erro ao excluir o atributo';
    useAlert(errorMessage, 'error');
  }
};

const copyToClipboard = async (text) => {
  if (!text) return;
  try {
    await copyTextToClipboard(text);
    useAlert('Copiado com sucesso');
  } catch (error) {
    useAlert('Erro ao copiar o texto', 'error');
  }
};

const getListOptions = (values) => {
  return values.map((value, index) => ({
    id: index + 1,
    name: value,
  }));
};

const getSelectedItem = (attribute) => {
  if (!attribute.value) {
    return null;
  }
  const id = attribute.attribute_values.indexOf(attribute.value) + 1;
  return { id, name: attribute.value };
};

const addAttribute = async (attr) => {
  if (!props.contact?.id) return;
  
  // Don't save to backend yet, just add to temporary editing state
  showAddDropdown.value = false;
  
  // Create a temporary attribute object
  const tempAttribute = {
    key: attr.attribute_key,
    value: '',
    attribute_display_name: attr.attribute_display_name || attr.attribute_key,
    attribute_display_type: attr.attribute_display_type || 'text',
    attribute_description: attr.attribute_description || '',
    attribute_values: attr.attribute_values || [],
    regex_pattern: attr.regex_pattern || null,
    regex_cue: attr.regex_cue || null,
  };
  
  // For list type, don't set initial value
  // Leave it empty so user can select
  
  // Add to temporary attributes list
  tempAttributes.value.push(tempAttribute);
  
  // For checkbox, save immediately with true value
  if (attr.attribute_display_type === 'checkbox') {
    const updatedAttributes = { 
      ...customAttributes.value, 
      [attr.attribute_key]: true 
    };
    
    try {
      await store.dispatch('contacts/update', {
        id: props.contact.id,
        custom_attributes: updatedAttributes,
      });
      useAlert('Atributo adicionado com sucesso');
      // Remove from temp after successful save
      tempAttributes.value = tempAttributes.value.filter(t => t.key !== attr.attribute_key);
    } catch (error) {
      const errorMessage =
        error?.response?.message || 'Erro ao adicionar o atributo';
      useAlert(errorMessage, 'error');
      // Remove from temp on error
      tempAttributes.value = tempAttributes.value.filter(t => t.key !== attr.attribute_key);
    }
  } else if (attr.attribute_display_type !== 'list') {
    // Start editing immediately for non-checkbox and non-list types
    nextTick(() => {
      startEditing(tempAttribute);
    });
  }
};

// Modal methods
const openAttributeModal = (attribute) => {
  selectedAttribute.value = attribute;
  showAttributeModal.value = true;
};

const closeAttributeModal = () => {
  selectedAttribute.value = null;
  showAttributeModal.value = false;
};

const handleAttributeSave = (saveData) => {
  // Remove the temporary attribute if it was saved successfully or deleted
  if (saveData && saveData.key) {
    tempAttributes.value = tempAttributes.value.filter(t => t.key !== saveData.key);
  }
  // Close the modal
  closeAttributeModal();
};

    return {
      // Reactive state
      editingAttribute,
      editedValue,
      showAddDropdown,
      tempAttributes,
      showAttributeModal,
      selectedAttribute,
      // Computed
      attributes,
      customAttributes,
      attributesWithMetadata,
      displayedAttributes,
      unusedAttributes,
      // Methods
      getFormattedAttributeValue,
      getFormattedEditValue,
      getAttributeIcon,
      getInputType,
      startEditing,
      cancelEditing,
      updateCustomAttribute,
      updateCheckbox,
      updateListValue,
      deleteCustomAttribute,
      copyToClipboard,
      getListOptions,
      getSelectedItem,
      addAttribute,
      openAttributeModal,
      closeAttributeModal,
      handleAttributeSave,
      isValidURL,
    };
  },
};
</script>

<template>
  <div>
    <!-- Display each attribute with ContactInfo layout -->
    <div
      v-for="(attribute, index) in displayedAttributes"
      :key="attribute.key"
      class="py-1 px-1"
      :class="{ 'border-t border-n-weak': index > 0 }"
    >
      <div 
        class="flex items-start hover:bg-n-slate-3 transition-colors rounded-lg p-3 cursor-pointer group"
        @click="openAttributeModal(attribute)"
      >
        <div class="w-6 h-6 mr-3 relative flex-shrink-0 mt-0.5">
          <span :class="getAttributeIcon(attribute.attribute_display_type)" class="absolute inset-0 m-auto opacity-100 transition-opacity duration-200 group-hover:opacity-0 text-slate-500 dark:text-slate-400" />
          <span class="i-lucide-edit absolute inset-0 m-auto text-n-brand opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
        </div>
        
        <div class="flex-grow min-w-0">
          <div class="text-sm font-medium text-slate-700 dark:text-slate-200 break-words">
            {{ attribute.attribute_display_name }}
          </div>
          
          <!-- Value display -->
          <div class="text-sm text-slate-500 dark:text-slate-400 mt-0.5 break-words">
            <!-- Checkbox type -->
            <span v-if="attribute.attribute_display_type === 'checkbox'">
              {{ getFormattedAttributeValue(attribute) ? 'Sim' : 'Não' }}
            </span>
            <!-- Link type with valid URL -->
            <span
              v-else-if="attribute.attribute_display_type === 'link' && attribute.value && isValidURL(attribute.value)"
              class="text-blue-600 dark:text-blue-400"
            >
              {{ getFormattedAttributeValue(attribute) }}
            </span>
            <!-- Other types -->
            <span v-else>
              {{ getFormattedAttributeValue(attribute) || '---' }}
            </span>
          </div>
        </div>
      </div>
      
      <!-- Legacy actions (hidden, kept for compatibility) -->
      <div class="hidden">
        <div
          v-if="attribute.attribute_description"
          v-tooltip.top="attribute.attribute_description"
          class="text-slate-500 dark:text-slate-400 cursor-help p-1"
        >
          <span class="i-lucide-info text-sm" />
        </div>
        <div
          v-if="attribute.value && attribute.attribute_display_type !== 'checkbox'"
          v-tooltip.top="'Copiar valor'"
          class="text-slate-500 dark:text-slate-400 cursor-pointer p-1"
          @click="copyToClipboard(attribute.value)"
        >
          <span class="i-lucide-copy text-sm" />
        </div>
        <div
          v-if="attribute.attribute_display_type !== 'checkbox'"
          v-tooltip.top="'Editar'"
          class="text-slate-500 dark:text-slate-400 cursor-pointer p-1"
          @click="startEditing(attribute)"
        >
          <span class="i-lucide-edit text-sm" />
        </div>
        <div
          v-if="attribute.value"
          v-tooltip.top="'Excluir'"
          class="text-slate-500 dark:text-slate-400 cursor-pointer p-1"
          @click="deleteCustomAttribute(attribute.key)"
        >
          <span class="i-lucide-trash text-sm" />
        </div>
      </div>
    </div>
    
    <!-- Add attribute button -->
    <div 
      v-if="unusedAttributes.length > 0"
      class="relative"
    >
      <div class="flex items-center justify-center cursor-pointer bg-n-slate-3/50 hover:bg-n-slate-3 transition-all rounded-lg p-3 gap-2 text-slate-500 dark:text-slate-300" @click="showAddDropdown = !showAddDropdown">
        <!-- Icon -->
        <span class="i-lucide-plus" />
        
        <!-- Title -->
        <span class="text-sm font-medium">
          Adicionar atributo
        </span>
      </div>
      
      <!-- Dropdown -->
      <div
        v-if="showAddDropdown"
        v-on-clickaway="() => showAddDropdown = false"
        class="absolute bottom-full left-4 right-4 mb-1 z-50 max-h-[12.5rem] shadow-xl rounded-xl border border-slate-100 bg-white dark:bg-slate-800 dark:border-slate-700 overflow-auto scrollbar-attribute-list"
      >
        <div class="px-1 py-1">
          <div
            v-for="attr in unusedAttributes"
            :key="attr.attribute_key"
            class="w-full px-3 py-2 rounded-md text-sm text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-600 flex items-center transition-colors cursor-pointer"
            @click="addAttribute(attr)"
          >
            <span :class="getAttributeIcon(attr.attribute_display_type)" class="mr-2 text-sm flex-shrink-0 text-slate-500 dark:text-slate-400" />
            <span class="flex-grow text-left">{{ attr.attribute_display_name || attr.attribute_key }}</span>
            <span 
              v-if="attr.attribute_description"
              v-tooltip.left="attr.attribute_description"
              class="i-lucide-info text-xs text-slate-400 dark:text-slate-500 cursor-help ml-1"
            />
          </div>
          <div
            v-if="unusedAttributes.length === 0"
            class="flex justify-center py-4 px-2.5 font-medium text-xs text-slate-700 dark:text-slate-200"
          >
            {{ searchAttribute ? 'Nenhum atributo encontrado' : 'Nenhum atributo disponível' }}
          </div>
        </div>
      </div>
    </div>
    
    <!-- Empty state -->
    <div
      v-if="!attributesWithMetadata.length && unusedAttributes.length === 0"
      class="flex items-center p-3 text-slate-500 dark:text-slate-400"
    >
      <div class="w-6 text-center mr-3">
        <span class="i-lucide-tag" />
      </div>
      <div class="flex-grow">
        <div class="text-sm">
          Sem dados do cliente
        </div>
      </div>
    </div>
    
    <!-- Attribute Info Modal -->
    <AttributeInfoModal
      :show="showAttributeModal"
      :attribute="selectedAttribute"
      :contact="contact"
      @close="closeAttributeModal"
      @save="handleAttributeSave"
    />
  </div>
</template>

<style lang="scss" scoped>
/* Custom scrollbar styles for attribute list */
.scrollbar-attribute-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.scrollbar-attribute-list::-webkit-scrollbar {
  width: 8px;
}

.scrollbar-attribute-list::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

.scrollbar-attribute-list::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  transition: background-color 0.3s ease;
}

.scrollbar-attribute-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.scrollbar-attribute-list::-webkit-scrollbar-thumb:active {
  background-color: rgba(0, 0, 0, 0.4);
}

/* Dark theme */
.dark .scrollbar-attribute-list {
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.dark .scrollbar-attribute-list::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
}

.dark .scrollbar-attribute-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.dark .scrollbar-attribute-list::-webkit-scrollbar-thumb:active {
  background-color: rgba(255, 255, 255, 0.4);
}
</style>